import React, {Suspense, useCallback, useContext, useEffect} from "react";
import {notification}                   from "antd";
import * as webSocket from "@tools/webSocket/webSocket";
import {capitalizeFirstLetter} from "@tools/capitalizeFirstLetter";
import {GlobalContext} from "@tools/globalContext";
import {useTranslation} from "react-i18next";
import useApi from "@hooks/useApi";
import {useEventEmitter} from "@hooks/useEventEmitter";
import {EGridEvent} from "@interface/enums";
import apiConfig from "./apiConfig";
import wait from "@tools/wait";


const fieldsPath = "hydraExtension/notificationsMessages";

const useNotificationController = () => {
    const { user, updateNotifications, createNotifications, notifications } = useContext(GlobalContext);
    const {t} = useTranslation()
    const { emit, useListener } = useEventEmitter();
    const {crudApi} = useApi({config: apiConfig})
    const [api, contextHolder] = notification.useNotification();


    useEffect(() => {
        let ev: any = null;

        if(crudApi.readNotificationCount) {
            (async () => {
                const result = await crudApi.readNotificationCount?.({})
                createNotifications(result?.data?.reduce((res: {[key: string] : any}, item: any) => {
                    res[item.name as string] = item
                    return res;
                }, {}))
    
                const ev = webSocket.on("message", async data => {
                    const eventData = await emit("beforeSocketEvent", data)
                    let newData = eventData?.data || data;
                    const {type} = newData
    
                    if(type.indexOf("create_") === 0)
                        updateNotifications({name: type, count: {"action" :"inc", value: 1}})
                    else if(type.indexOf("update_") === 0)
                        updateNotifications({name: type.replace("update_", "create_"), count: {"action" :"inc", value: 1}})
                })
    
            })()
            
        }

        return () => {ev?.off?.()}
    }, [])

    useEffect(() => {
        const ev = webSocket.on("message", async data => {
            const {type} = data

            try {
                const Message = await import(`@root/${fieldsPath}/${capitalizeFirstLetter(type)}`)

                if(typeof Message.default === "function") {
                    const Description = Message.default
                    const Title = Message.Title

                    api.info({
                        message:  Title ? <Title user={user} data={data}/> : t(`notification:${type}.title`),
                        description: <Description user={user} data={data}/>,
                        duration : Message.duration || null
                    })
                }
                else {
                    const message = Message.default({user, t, data});
                    const notificationType: any = message.type || "info";
    
                    notification[notificationType]({
                        ...message.title && {message: message.title},
                        ...message.message && {description: message.message},
                        ...(message.duration || message.duration === null) &&  {duration: message.duration}
                    })
                }
            }
            catch(e) {
                // console.log(e)
            }
        })

        return () => {ev.off()}
    }, [user, t])

    const onReadData = useCallback(async (data: {index: string}) => {
        if(!crudApi.updateNotificationAsRead)
            return 
        if (notifications?.[`create_${data.index}`]?.count) {
            await crudApi.updateNotificationAsRead?.({index: data.index})
            updateNotifications({name: `create_${data.index}`, count: {"action" :"replace", value: 0}})
            await wait(100)
        } 
        if (notifications?.[`update_${data.index}`]?.count) {
            await crudApi.updateNotificationAsRead?.({index: data.index})
            updateNotifications({name: `update_${data.index}`, count: {"action" :"replace", value: 0}})
        }
    }, [notifications, crudApi])

    useListener(EGridEvent.afterRead, onReadData)

    return {contextHolder}
}

export default useNotificationController
